/* import __COLOCATED_TEMPLATE__ from './url-suggestion-group-section.hbs'; */
/* RESPONSIBLE TEAM: team-messenger */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type { SuggestionGroup, UrlGroupEntry } from './types';

const ERROR_CODE_TRANSLATION_KEYS: Record<string, string> = {
  knowledge_base_insufficient_content:
    'messenger.sales.smart-suggestions.error-codes.knowledge-base-insufficient-content',
  all_questions_filtered: 'messenger.sales.smart-suggestions.error-codes.all-questions-filtered',
  llm_no_suitable_suggestions:
    'messenger.sales.smart-suggestions.error-codes.llm-no-suitable-suggestions',
  llm_service_temporary_failure:
    'messenger.sales.smart-suggestions.error-codes.llm-service-temporary-failure',
  input_validation_failed: 'messenger.sales.smart-suggestions.error-codes.input-validation-failed',
  internal_unexpected_error:
    'messenger.sales.smart-suggestions.error-codes.internal-unexpected-error',
  generation_failed: 'messenger.sales.smart-suggestions.error-codes.generation-failed',
  scrape_no_content: 'messenger.sales.smart-suggestions.error-codes.scrape-no-content',
  scrape_failed: 'messenger.sales.smart-suggestions.error-codes.scrape-failed',
};

interface Args {
  urlGroup: UrlGroupEntry;
  isDefault: boolean;
  isExpanded: boolean;
  isGeneratingSuggestions: boolean;
  generatingForPattern: string | null | undefined;
  showAllSuggestions: boolean;
  hasUnsavedSuggestions: boolean;
  failedGenerationUrl: string;
  failedGenerationErrorMessage: string;
  failedGenerationErrorCode: string;
  isGenerationErrorDismissed: boolean;
  websiteUrl: string;
  isEditingWebsiteUrl: boolean;
  generationJobStatus: string | null;
  onToggleExpanded: () => void;
  onToggleShowAll: () => void;
  onRemoveSuggestion: (groupUuid: string) => void;
  onRemoveUrlGroup: () => void;
  onStartEditingUrl: () => void;
  onCancelEditingUrl: () => void;
  onUpdateWebsiteUrl: (event: Event) => void;
  onGenerate: () => void;
  onRetryFailedGeneration: () => void;
  onDismissGenerationError: () => void;
}

interface Signature {
  Element: HTMLDivElement;
  Args: Args;
}

export default class UrlSuggestionGroupSection extends Component<Signature> {
  @service declare intl: IntlService;

  get isThisGroupGenerating(): boolean {
    if (!this.args.isGeneratingSuggestions) {
      return false;
    }
    // null = default group is generating, 'per-url' = a per-URL group is generating
    if (this.args.isDefault) {
      return this.args.generatingForPattern === null;
    }
    return this.args.generatingForPattern === 'per-url';
  }

  get suggestions(): SuggestionGroup[] {
    return this.args.urlGroup.suggestions;
  }

  get displayedGroups(): SuggestionGroup[] {
    if (this.args.showAllSuggestions) {
      return this.suggestions;
    }
    return this.suggestions.slice(0, 3);
  }

  get hasGroupData(): boolean {
    return this.suggestions.length > 0;
  }

  get hasMoreSuggestions(): boolean {
    return this.suggestions.length > 3;
  }

  get remainingCount(): number {
    return this.suggestions.length - 3;
  }

  get hasNotEnoughSuggestions(): boolean {
    return this.suggestions.length > 0 && this.suggestions.length < 3;
  }

  get displayUrl(): string {
    let url = this.args.websiteUrl || this.args.urlGroup.websiteUrl || '';
    return url.replace(/^https?:\/\//, '').replace(/\/+$/, '');
  }

  get headerLabel(): string {
    if (this.args.isDefault) {
      return this.intl.t('messenger.sales.smart-suggestions.default-website-url-label');
    }
    return this.intl.t('messenger.sales.smart-suggestions.url-group-label');
  }

  get hasCompletedUrl(): boolean {
    let status = this.args.generationJobStatus;
    return Boolean((status === 'completed' || status === 'failed') && this.args.websiteUrl);
  }

  get showUrlDisplay(): boolean {
    if (this.args.isDefault) {
      return this.hasCompletedUrl && !this.args.isEditingWebsiteUrl && !this.isThisGroupGenerating;
    }
    return !this.args.isEditingWebsiteUrl && !this.isThisGroupGenerating;
  }

  get showUrlInput(): boolean {
    if (this.isThisGroupGenerating) {
      return false;
    }
    if (this.args.isDefault) {
      return !this.hasCompletedUrl || this.args.isEditingWebsiteUrl;
    }
    return this.args.isEditingWebsiteUrl;
  }

  get showCancelButton(): boolean {
    return this.args.isEditingWebsiteUrl;
  }

  get isGenerateDisabled(): boolean {
    return !this.args.websiteUrl.trim() || this.args.isGeneratingSuggestions;
  }

  get effectiveExpanded(): boolean {
    if (this.showUrlInput || this.isThisGroupGenerating) {
      return true;
    }
    return this.args.isExpanded;
  }

  get hasGenerationError(): boolean {
    return Boolean(this.args.failedGenerationUrl && !this.args.isGenerationErrorDismissed);
  }

  get failedDisplayUrl(): string {
    return this.args.failedGenerationUrl.replace(/^https?:\/\//, '').replace(/\/+$/, '');
  }

  get translatedErrorMessage(): string | null {
    let errorCode = this.args.failedGenerationErrorCode;
    if (errorCode) {
      let key = ERROR_CODE_TRANSLATION_KEYS[errorCode];
      if (key && this.intl.exists(key)) {
        return this.intl.t(key);
      }
    }
    return this.args.failedGenerationErrorMessage || null;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'MessengerSettings::Sales::UrlSuggestionGroupSection': typeof UrlSuggestionGroupSection;
  }
}
